// src/pages/DocumentsPage.tsx
import React, { useState } from 'react';
import { Table, Button, Modal, Form, Select, Upload, Typography, message, Space, Tooltip, Input, Tag } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import { mockDocuments, mockClients } from '../data/mockData';
import { Document, DocumentCategory } from '../types';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';

const { Title } = Typography;

const DocumentsPage: React.FC = () => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const [documents, setDocuments] = useState<Document[]>(mockDocuments);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'all'>('all');

  // Filtrar documentos según el rol
  const filteredDocuments = currentUser?.role === 'admin'
    ? documents
    : documents.filter(d => d.clientId === currentUser?.clientId);

  // Aplicar búsqueda y filtro de categoría
  const searchedDocuments = filteredDocuments.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchText.toLowerCase()) ||
                         getClientName(doc.clientId).toLowerCase().includes(searchText.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getClientName = (clientId: string) => {
    const client = mockClients.find(c => c.id === clientId);
    return client?.companyName || 'N/A';
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Categoría',
      dataIndex: 'category',
      key: 'category',
      render: (category: DocumentCategory) => (
        <Tag color={
          category === 'Contratos' ? 'blue' :
          category === 'Políticas' ? 'green' :
          category === 'Auditorías' ? 'orange' :
          category === 'Reportes' ? 'purple' : 'default'
        }>
          {category}
        </Tag>
      ),
    },
    ...(currentUser?.role === 'admin' ? [{
      title: 'Cliente',
      dataIndex: 'clientId',
      key: 'clientId',
      render: (clientId: string) => getClientName(clientId),
    }] : []),
    {
      title: 'Versión',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Fecha de Carga',
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: (date: string) => new Date(date).toLocaleDateString('es-ES'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_: any, record: Document) => (
        <Space size="small">
          <Tooltip title="Ver documento">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDocument(record)}
            />
          </Tooltip>
          <Tooltip title="Descargar">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => message.success(`Descargando: ${record.name}`)}
            />
          </Tooltip>
          {currentUser?.role === 'admin' && (
            <Tooltip title="Eliminar">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteDocument(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleUpload = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setFileList([]);
    form.resetFields();
  };

  const handleSubmit = (values: { clientId: string }) => {
    if (fileList.length === 0) {
      message.error('Por favor seleccione un archivo');
      return;
    }

    const newDocument: Document = {
      id: `d${documents.length + 1}`,
      name: fileList[0].name,
      clientId: values.clientId,
      category: 'Otros',
      version: 1,
      uploadedAt: new Date().toISOString(),
      uploadedBy: currentUser?.id || 'u1',
      fileUrl: '#',
    };

    setDocuments([...documents, newDocument]);
    message.success('Documento subido exitosamente');
    setIsModalOpen(false);
    setFileList([]);
    form.resetFields();
  };

  const handleViewDocument = (document: Document) => {
    setSelectedDocument(document);
    setIsViewModalOpen(true);
  };

  const handleDeleteDocument = (document: Document) => {
    modal.confirm({
      title: '¿Está seguro de eliminar este documento?',
      icon: <ExclamationCircleOutlined />,
      content: `Se eliminará el documento "${document.name}". Esta acción no se puede deshacer.`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        const updatedDocuments = documents.filter(d => d.id !== document.id);
        setDocuments(updatedDocuments);
        message.success('Documento eliminado exitosamente');
      },
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>
          {currentUser?.role === 'admin' ? 'Gestión de Documentos' : 'Mis Documentos'}
        </Title>
        {currentUser?.role === 'admin' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleUpload}>
            Subir Documento
          </Button>
        )}
      </div>

      {/* Búsqueda y Filtros */}
      <Space style={{ marginBottom: 16, width: '100%' }}>
        <Input.Search
          placeholder="Buscar documentos por nombre o cliente..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          allowClear
        />
        <Select
          value={categoryFilter}
          onChange={setCategoryFilter}
          style={{ width: 180 }}
        >
          <Select.Option value="all">Todas las categorías</Select.Option>
          <Select.Option value="Contratos">Contratos</Select.Option>
          <Select.Option value="Políticas">Políticas</Select.Option>
          <Select.Option value="Auditorías">Auditorías</Select.Option>
          <Select.Option value="Reportes">Reportes</Select.Option>
          <Select.Option value="Otros">Otros</Select.Option>
        </Select>
      </Space>

      <Table
        columns={columns}
        dataSource={searchedDocuments}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {currentUser?.role === 'admin' && (
        <Modal
          title="Subir Nuevo Documento"
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Cliente"
              name="clientId"
              rules={[{ required: true, message: 'Por favor seleccione un cliente' }]}
            >
              <Select placeholder="Seleccione un cliente">
                {mockClients.map(client => (
                  <Select.Option key={client.id} value={client.id}>
                    {client.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Archivo">
              <Upload.Dragger
                fileList={fileList}
                beforeUpload={(file) => {
                  setFileList([file]);
                  return false; // Prevenir subida automática
                }}
                onRemove={() => setFileList([])}
                maxCount={1}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Haga clic o arrastre el archivo aquí</p>
                <p className="ant-upload-hint">Soporte para archivos PDF, DOCX, etc.</p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Subir Documento
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Modal de Vista Previa */}
      <Modal
        title="Detalles del Documento"
        open={isViewModalOpen}
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="download" type="primary" icon={<DownloadOutlined />}>
            Descargar
          </Button>,
          <Button key="close" onClick={() => setIsViewModalOpen(false)}>
            Cerrar
          </Button>
        ]}
      >
        {selectedDocument && (
          <div>
            <p><strong>Nombre:</strong> {selectedDocument.name}</p>
            <p><strong>Categoría:</strong> <Tag color={
              selectedDocument.category === 'Contratos' ? 'blue' :
              selectedDocument.category === 'Políticas' ? 'green' :
              selectedDocument.category === 'Auditorías' ? 'orange' :
              selectedDocument.category === 'Reportes' ? 'purple' : 'default'
            }>{selectedDocument.category}</Tag></p>
            <p><strong>Cliente:</strong> {getClientName(selectedDocument.clientId)}</p>
            <p><strong>Versión:</strong> {selectedDocument.version}</p>
            <p><strong>Fecha de Carga:</strong> {new Date(selectedDocument.uploadedAt).toLocaleString('es-ES')}</p>
            <p><strong>ID:</strong> {selectedDocument.id}</p>
          </div>
        )}
      </Modal>

      {contextHolder}
    </div>
  );
};

export default DocumentsPage;
